import * as React from 'react';
import { useLocation } from 'react-router-dom';
import { useSession } from '@/hooks/useSession';
import { hasPlayfulNavProfile } from '@/hooks/usePlayfulNav';
import { usePlatformAbilities } from '@/hooks/usePlatformAbilities';
import tiaAvatar from '@/assets/images/tia/tia-avatar-3d.png';
import { TiaChatBar } from './bottom-bar-infantil/tia-chat-bar';
import { TiaChatSheet } from './bottom-bar-infantil/tia-chat-sheet';
import { useTiaChat, getUserLearningPathIdFromPathname } from './bottom-bar-infantil/use-tia-chat';

export function TiaChatWidget() {
  const { session } = useSession();
  const { abilities } = usePlatformAbilities() || {};
  if (!abilities?.['tia-chat'] || !hasPlayfulNavProfile(session?.user)) return null;
  return <FloatingChat />;
}

function FloatingChat() {
  const { pathname, search } = useLocation();
  const [open, setOpen] = React.useState(false);
  const [sheetOpen, setSheetOpen] = React.useState(false);
  const loadedHistory = React.useRef(false);
  const chat = useTiaChat({ userLearningPathId: getUserLearningPathIdFromPathname(pathname), currentPath: `${pathname}${search || ''}` });

  const close = () => {
    setOpen(false);
    setSheetOpen(false);
  };

  React.useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = event => {
      if (event.key === 'Escape') close();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open]);

  const toggle = () => {
    if (open) {
      close();
      return;
    }
    setOpen(true);
    if (!loadedHistory.current) {
      loadedHistory.current = true;
      chat.loadHistory();
    }
  };

  return (
    <>
      {sheetOpen && (
        <TiaChatSheet
          floating
          messages={chat.messages}
          status={chat.status}
          quota={chat.quota}
          onRetry={chat.retry}
          onClose={() => setSheetOpen(false)}
        />
      )}
      <div className="pointer-events-none fixed bottom-[calc(1.75rem+env(safe-area-inset-bottom))] right-[calc(1rem+env(safe-area-inset-right))] z-[103] flex w-[calc(100%-2rem-env(safe-area-inset-right))] max-w-[40.5rem] items-center justify-end gap-2 [&>*]:pointer-events-auto">
        {open && (
          <TiaChatBar
            floating
            onSubmit={body => {
              setSheetOpen(true);
              chat.send(body);
            }}
            onFocus={() => {
              if (chat.messages.length > 0) setSheetOpen(true);
            }}
            onClose={close}
            disabled={chat.status === 'thinking' || chat.status === 'loading'}
          />
        )}
        <button
          type="button"
          onClick={toggle}
          aria-label="Conversar com a T.I.A"
          aria-expanded={open}
          aria-controls={sheetOpen ? 'tia-chat-sheet' : undefined}
          className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full border-4 border-white bg-white shadow-lg transition-transform focus-visible:outline focus-visible:outline-2 focus-visible:outline-secondary active:scale-95"
        >
          <img
            src={tiaAvatar}
            alt=""
            className="h-full w-full rounded-full"
          />
        </button>
      </div>
    </>
  );
}
